import type { ReactNode } from "react";
import { Button } from "@legends/ui";
import { logout } from "./api";
import { LegendBrandMark } from "./sections";

// Shared chrome for the persona placeholder views (ADR 0007): a centered
// Meridian card stating its persona, plus either a SIGN IN link (public
// surfaces) or a Sign out button (authenticated personas) upper-right.

/** Small mono sign-in link pinned to the upper-right corner of the page. */
export function SignInLink({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label="Sign in"
      onClick={onClick}
      style={{
        position: "absolute",
        top: 14,
        right: 18,
        background: "none",
        border: "none",
        padding: 4,
        cursor: "pointer",
        fontFamily: "var(--font-mono)",
        fontSize: "var(--fs-2xs)",
        letterSpacing: "var(--ls-label)",
        textTransform: "uppercase",
        color: "var(--accent)",
      }}
    >
      Sign in ▸
    </button>
  );
}

export interface PersonaCardProps {
  /** Card heading, rendered mono/uppercase ("Legends · Legend"). */
  title: string;
  /** One-line purpose statement for the placeholder. */
  copy: string;
  /** Rendered at the right edge of the card header (e.g. the Sign out button). */
  headerRight?: ReactNode;
  /** When set, a SIGN IN link renders in the page's upper-right corner. */
  onSignIn?: () => void;
  /** Optional presentation hook for a persona surface that needs a responsive
   *  content width while preserving the default compact card. */
  contentClassName?: string;
  children?: ReactNode;
}

export function PersonaCard({ title, copy, headerRight, onSignIn, contentClassName, children }: PersonaCardProps) {
  return (
    <main
      className="mer-canvas mer-canvas--photo"
      style={{
        minHeight: "calc(100vh / var(--ui-zoom, 1))",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        padding: "32px 16px",
        boxSizing: "border-box",
      }}
    >
      {onSignIn && <SignInLink onClick={onSignIn} />}
      <div
        className={contentClassName}
        style={{
          background: "var(--surface-2)",
          border: "1px solid var(--border)",
          padding: "26px 32px 24px",
          width: "var(--persona-card-width, min(480px, 100%))",
          boxSizing: "border-box",
          animation: "cnPop var(--dur) var(--ease-out)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
          <LegendBrandMark />
          <h1
            style={{
              margin: 0,
              fontFamily: "var(--font-mono)",
              fontSize: "var(--fs-2xs)",
              fontWeight: 600,
              letterSpacing: "var(--ls-caps)",
              textTransform: "uppercase",
              color: "var(--text-label)",
            }}
          >
            {title}
          </h1>
          {headerRight && <span style={{ marginLeft: "auto" }}>{headerRight}</span>}
        </div>
        <p style={{ margin: "18px 0 0", fontSize: "var(--fs-body)", color: "var(--text-dim)" }}>
          {copy}
        </p>
        {children}
      </div>
    </main>
  );
}

export interface PersonaScreenProps {
  title: string;
  copy: string;
  onLogout: () => void;
}

/** An authenticated persona placeholder: card + Sign out upper-right. */
export default function PersonaScreen({ title, copy, onLogout }: PersonaScreenProps) {
  const signOut = () => {
    logout().finally(onLogout);
  };
  return (
    <PersonaCard
      title={title}
      copy={copy}
      headerRight={
        <Button size="sm" variant="muted" onClick={signOut}>
          Sign out
        </Button>
      }
    />
  );
}
